import styles from "./style/EnterpriseList.module.scss";
import InnerLayout from "../Layout/InnerLayout";
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { AiOutlineSetting } from "react-icons/ai";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { IoChevronDownOutline } from "react-icons/io5";
import Pagination from "../Utilities/PaginationNew";
import Toast from "../Utilities/Toast";
import { CgLoadbarDoc } from "react-icons/cg";
import {
  getEnterpriseCompanyListDetail,
  activateEnterpriseCompany,
  enableStartOnePlan,
  getEnterpriseCompanyList as getEnterpriseCompany,
  removeCompany,
  removeSuccess,
  selectEnterpriseCompany,
  getEnterpriseAssets,
  getEnterpriseUsage,
  activateEnterpriseService,
} from "../../store/slice/manager/enterpriseCompany";
import { selectPermissionSettings } from "../../store/slice/permissionSettings";
import { selectMenu } from "../../store/slice/manager/menu";
import Alert from "../Utilities/Alert";
import EmptyBox from "../Utilities/EmptyBox";
import LoadingTable from "../Utilities/LoadingTable";
import Button from "../Utilities/Button";
import Tips from "../Utilities/Tips";
import InputSearch from "../Utilities/InputSearch";
import ModalHistory from "./ModalHistory";
import UsagePanel from "./UsagePanel";
import PlanCard from "./PlanCard";
import PlanCardSkeleton from "./PlanCardSkeleton";
import { SEAT_SERVICE_IDS, isNoSeatServiceItem, noSeatUsageKey } from "./planCardForm";
import { sendGetUsageRequest, sendPutUsageRequest } from "../../api/helper";
import { resolveUsageEnabled, hasActiveService } from "./usageGate";
import { formatSeatLimit } from "../../constant/SEAT_LIMIT";
import { getServiceName } from "../../constant/SERVICE_NAME_MAP";
import GiftModal from "./GiftModal";
import { MdClear, MdPeople } from "react-icons/md";
import moment from "moment";
import { FiCheck, FiX } from "react-icons/fi";
import {
  putVerifyEmail,
  selectEnterprise,
  updateActiveService,
} from "../../store/slice/enterpriseLists";
import ModalUsers from "./ModalUsers";
import { LuTrash2 } from "react-icons/lu";
import TextField from "../Utilities/TextField";
import { generateRandomString } from "../../utils";
import Dropdown from "../Utilities/DropdownDynamic";
// 「購買方案」三桶計數一律由後端 plan_counts 提供（見 EnterpriseServiceListServices::planCounts），
// 母體與狀態判定與管理方案彈窗的服務卡片完全相同——含停用模組、START-ONE 與非 admin 權限的排除，
// 因此三個數字之和恆等於彈窗裡的卡片數。前端不再以 menu 為母體自行比對 subscribed_services。
//   已啟用 ← 已開通且訂閱中
//   試用中 ← 已開通且試用中
//   未啟用 ← 未開通／已停用，或未購買／未續訂（彈窗四種徽章合併為一桶）
const EMPTY_PLAN_COUNTS = { active: 0, trial: 0, off: 0 };
const getPlanCounts = (data) => data?.plan_counts ?? EMPTY_PLAN_COUNTS;

const PlanData = React.memo(
  ({ item, auth, permissions, menu, reportCard, usageEnabled }) => {
    return item?.map((data, index) => (
      <PlanCard
        key={data?.software_services_id ?? index}
        data={data}
        auth={auth}
        permissions={permissions}
        menu={menu}
        reportCard={reportCard}
        usageEnabled={usageEnabled}
      />
    ));
  }
);


// 管理方案彈窗：把原本展開的各服務資訊，一個服務一張卡片列出（不分組）
// 存檔採「全部儲存」：蒐集各服務卡片 + 用量設定，一次批次送出 POST .../activate。
const ManagePlanModal = ({
  isOpen,
  onClose,
  company,
  detailData,
  detailPlanData,
  loading,
  planDataProps,
  usageData,
  usageLoading,
  serviceQuotas,
}) => {
  const dispatch = planDataProps?.dispatch;
  const reloadDetail = planDataProps?.reloadDetail;
  const reloadUsage = planDataProps?.reloadUsage;
  const reloadQuotas = planDataProps?.reloadQuotas;
  const enterpriseId = detailData?.enterprise_id;

  // 各服務卡片回報：{ [service_id]: { serviceName, payload, valid, errors, dirty, activeSeat } }
  const [cardMap, setCardMap] = useState({});
  // 用量面板回報：{ payload, valid, errors, dirty }
  const [usageInfo, setUsageInfo] = useState(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const reportCard = useCallback((id, info) => {
    if (id == null) return;
    setCardMap((prev) => {
      if (info == null) {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: info };
    });
  }, []);

  const reportUsage = useCallback((info) => setUsageInfo(info), []);

  // 啟用中服務席位上限總和（連動用量配額基數）
  const activeSeatTotal = Object.values(cardMap).reduce(
    (sum, c) => sum + (Number(c?.activeSeat) || 0),
    0
  );

  // 使用者主動調整（席位/啟停）才遞增的 nonce：用量面板據此自動帶入建議配額，
  // 避免開窗初次載入（卡片陸續回報使總和 0→實值）誤觸而讓用量變 dirty。
  const anyCardDirty = Object.values(cardMap).some((c) => c?.dirty);
  const [seatNonce, setSeatNonce] = useState(0);
  const seatTotalRef = useRef(activeSeatTotal);
  useEffect(() => {
    if (seatTotalRef.current === activeSeatTotal) return;
    seatTotalRef.current = activeSeatTotal;
    // 只有在已有卡片變更（使用者操作）時才視為使用者驅動的席位變動
    if (anyCardDirty) setSeatNonce((n) => n + 1);
  }, [activeSeatTotal, anyCardDirty]);

  // 用量管理 ↔ 服務啟用的雙向連動，一律看彈窗當前（未儲存）狀態（見 usageGate.js）
  const usageEnabledLive = resolveUsageEnabled(usageInfo, usageData);
  const anyServiceActive = hasActiveService(cardMap);

  // 未儲存變更清單（含用量），供關窗確認與「全部儲存」啟用判斷
  const dirtyTitles = Object.values(cardMap)
    .filter((c) => c?.dirty)
    .map((c) => c.serviceName);
  if (usageInfo?.dirty) dirtyTitles.push('用量管理');
  const anyDirty = dirtyTitles.length > 0;

  const requestClose = () => {
    if (savingAll) return;
    if (anyDirty) setConfirmClose(true);
    else onClose();
  };

  const discardAndClose = () => {
    setConfirmClose(false);
    setCardMap({});
    setUsageInfo(null);
    onClose();
  };

  // 「全部儲存」：先驗證（有變更的）服務與用量，通過後跳出確認彈窗列出修改項目，
  // 使用者確認後才真正送出（doSaveAll）。
  const handleSaveAll = () => {
    const dirtyCards = Object.values(cardMap).filter((c) => c.dirty);
    const invalid = dirtyCards.find((c) => !c.valid);
    if (invalid) {
      const firstErr = invalid.errors ? Object.values(invalid.errors)[0] : null;
      setErrorMsg(`「${invalid.serviceName}」${firstErr || '設定有誤，請確認後再試'}`);
      return;
    }
    const usageChanged = !!usageInfo?.dirty;
    if (usageChanged && !usageInfo.valid) {
      const firstErr = usageInfo.errors ? Object.values(usageInfo.errors)[0] : null;
      setErrorMsg(`用量管理：${firstErr || '設定有誤，請確認後再試'}`);
      return;
    }

    if (dirtyCards.length === 0 && !usageChanged) {
      onClose();
      return;
    }
    // 驗證通過、確有變更 → 跳確認彈窗（列出修改項目），確認後才 fetch
    setConfirmSave(true);
  };

  // 確認送出：只送有變更（dirty）的服務 +（有變更的）用量
  // （單一 transaction，後端原子寫入）。
  const doSaveAll = () => {
    const dirtyCards = Object.values(cardMap).filter((c) => c.dirty);
    const usageChanged = !!usageInfo?.dirty;
    const services = dirtyCards.map((c) => c.payload);
    const body = { enterprise_id: enterpriseId, services };
    if (usageChanged && usageInfo?.payload) body.usage = usageInfo.payload;

    // 年度方案服務（資料中心 / 任務自動化）的容量上限（GB）寫 usage-api 的
    // enterprise_service_quotas —— 與 client-api /activate 是不同後端，故分開送。
    // usage-api 失敗不阻斷方案儲存：標記後統一提示，方案照存。
    const quotaWrites = dirtyCards
      .filter((c) => c.usageKey && Number.isFinite(c.quotaGb))
      .map((c) =>
        sendPutUsageRequest(`/api/orgs/${enterpriseId}/service-quota`, {
          service: c.usageKey,
          quotaGb: c.quotaGb,
        })
          .then(() => ({ ok: true }))
          .catch(() => ({ ok: false }))
      );

    setConfirmSave(false);
    setSavingAll(true);
    Promise.all([dispatch(activateEnterpriseService(body)), ...quotaWrites]).then((results) => {
      setSavingAll(false);
      const e = results[0];
      const quotaFailed = results.slice(1).some((r) => r && r.ok === false);
      if (e?.payload?.msg === 'success') {
        reloadDetail && reloadDetail();
        reloadUsage && reloadUsage();
        reloadQuotas && reloadQuotas();
        if (quotaFailed) {
          setErrorMsg('方案已儲存，但部分「容量上限（GB）」更新失敗，請稍後再試');
        }
      } else {
        setErrorMsg(
          (typeof e?.payload?.data === 'string' && e.payload.data) ||
            '儲存失敗，請確認設定後再試一次'
        );
      }
    });
  };

  // START-ONE(id 8) 不在管理方案彈窗顯示（隱藏該服務卡片）；
  // 列表「購買方案」計數同樣排除它（後端 EnterpriseServiceListServices::MANAGE_PLAN_HIDDEN_SERVICE_IDS），
  // 兩邊要一起改才不會讓計數與卡片數對不上。
  // 席位制服務（SEAT_SERVICE_IDS）排在最前面。
  // 另：把 usage-api 的容量上限（enterprise_service_quotas）merge 進無席位服務的 data.quota_gb，
  // 供 PlanCard 顯示「容量上限（GB）」輸入。useMemo 保持物件參照穩定 —— 否則每次 render 產生
  // 新的 data 物件會讓 PlanCard 的重置 effect 反覆觸發、把使用者的編輯洗掉。
  const menuForQuota = planDataProps?.menu;
  const services = useMemo(() => {
    const menu = Array.isArray(menuForQuota) ? menuForQuota : [];
    // 自動化任務（sys_alias automation，本地 id 18）同樣不顯示：功能已併入任務管理，
    // 這個服務開不開通都不影響任何功能，留著只會讓管理者以為「開了才有自動化」。
    // 以 menu 的 sys_alias 判斷、id 為保底（software_services.id 各環境不保證一致）。
    const isAutomation = (id) => {
      const alias = menu.find((m) => m.id === id)?.sys_alias;
      return alias ? alias === 'automation' : id === 18;
    };
    const base = (Array.isArray(detailPlanData) ? detailPlanData : [])
      .filter((s) => s?.software_services_id !== 8 && !isAutomation(s?.software_services_id))
      .map((s, idx) => ({ s, idx }))
      .sort((a, b) => {
        const seatA = SEAT_SERVICE_IDS.includes(a.s?.software_services_id) ? 0 : 1;
        const seatB = SEAT_SERVICE_IDS.includes(b.s?.software_services_id) ? 0 : 1;
        return seatA - seatB || a.idx - b.idx;
      })
      .map(({ s }) => s);
    return base.map((s) => {
      const menuItem = menu.find((m) => m.id === s?.software_services_id);
      if (!isNoSeatServiceItem(menuItem, s?.software_services_id)) return s;
      const key = noSeatUsageKey(menuItem, s?.software_services_id);
      const quota_gb = key && serviceQuotas ? serviceQuotas[key] : undefined;
      return { ...s, quota_gb };
    });
  }, [detailPlanData, serviceQuotas, menuForQuota]);

  if (!isOpen) return null;

  return (
    <div
      className={styles.manageOverlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}>
      <div className={styles.manageModal}>
        <div className={styles.manageHead}>
          <div>
            <div className={styles.manageTitle}>
              {company?.enterprise_name} · 管理方案
            </div>
            <div className={styles.manageSub}>
              統一編號 {company?.tax_id} ・ 管理服務啟用狀態
            </div>
          </div>
          <Button type='tertiary' icon={<MdClear />} onClick={requestClose} />
        </div>

        <div className={styles.manageBody}>
          {/* 載入骨架統一做在 manageBody 層（用量 + 方案一起）；載完才渲染實際內容 */}
          {loading || usageLoading ? (
            <>
              {Array.from({ length: 4 }).map((_, idx) => (
                <PlanCardSkeleton key={idx} />
              ))}
            </>
          ) : (
            <>
              <UsagePanel
                usageData={usageData}
                activeSeatTotal={activeSeatTotal}
                seatNonce={seatNonce}
                reportUsage={reportUsage}
                anyServiceActive={anyServiceActive}
              />
              {services.length === 0 ? (
                <EmptyBox caption={"目前沒有可管理的方案"} />
              ) : (
                <PlanData
                  item={services}
                  reportCard={reportCard}
                  usageEnabled={usageEnabledLive}
                  {...planDataProps}
                />
              )}
            </>
          )}
        </div>

        <div className={styles.manageFoot}>
          {/* 委託單列表：待 customized_orders 補上 enterprise_id 後再實作，暫以 disabled 佔位 */}
          <Button title='委託單' type='secondary' disabled={true} />
          <Button
            title={savingAll ? '儲存中…' : '全部儲存'}
            type='primary'
            onClick={handleSaveAll}
            disabled={savingAll || !anyDirty}
          />
        </div>
      </div>

      {errorMsg && (
        <Alert
          isWarning={true}
          isOpen={!!errorMsg}
          mainTitle={errorMsg}
          rightButtonText='確定'
          onConfirm={() => setErrorMsg('')}
          onClose={() => setErrorMsg('')}
        />
      )}

      {confirmClose && (
        <Alert
          isWarning={true}
          isOpen={confirmClose}
          mainTitle='尚有未儲存的變更'
          leftButtonText='取消'
          rightButtonText='捨棄變更'
          onClose={() => setConfirmClose(false)}
          onConfirm={discardAndClose}>
          <ul
            style={{
              margin: "0 auto",
              paddingLeft: "20px",
              display: "inline-block",
              textAlign: "left",
              listStyleType: "disc",
            }}>
            {dirtyTitles.map((title, idx) => (
              <li key={idx} style={{ lineHeight: 1.5 }}>
                {title}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {confirmSave && (
        <Alert
          isOpen={confirmSave}
          mainTitle='確認儲存以下變更'
          leftButtonText='取消'
          rightButtonText='確認儲存'
          onClose={() => setConfirmSave(false)}
          onConfirm={doSaveAll}>
          <ul
            style={{
              margin: "0 auto",
              paddingLeft: "20px",
              display: "inline-block",
              textAlign: "left",
              listStyleType: "disc",
            }}>
            {dirtyTitles.map((title, idx) => (
              <li key={idx} style={{ lineHeight: 1.5 }}>
                {title}
              </li>
            ))}
          </ul>
        </Alert>
      )}
    </div>
  );
};

const EnterpriseList = () => {
  const params = useParams();
  const location = useLocation();
  const openEnterpriseHandled = useRef(false);
  const highlightRowRef = useRef(null);
  const [highlightEnterpriseId, setHighlightEnterpriseId] = useState(null);
  const dispatch = useDispatch();
  const {
    business,
    isSuccess,
    isGiftSuccess,
    isGiftPointsSuccess,
    error,
    loading,
    permission_error,
    enterpriseAssets,
  } = useSelector(selectEnterpriseCompany);

  const { detail } = useSelector(selectMenu);
  const { auth, permissions } = useSelector(selectPermissionSettings);
  const { menu } = useSelector(selectMenu);
  const enterprise = useSelector(selectEnterprise);

  const pageLimit = 20;

  const [searchTerm, setSearchTerm] = useState("");
  const [detailData, setDetailData] = useState({});
  const [detailPlanData, setDetailPlanData] = useState([]);
  const [fetchSearchValue, setFetchSearchValue] = useState("");
  const [index, setIndex] = useState();
  const [status, setStatus] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [enterpriseId, setEnterpriseId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [page, setPage] = useState(0);
  const [openTrialPlanModal, setOpenTrialPlanModal] = useState("");
  const [openGiftModal, setOpenGiftModal] = useState("");
  const [company, setCompany] = useState("");
  const [isOpenModalUser, setIsOpenModalUser] = useState(false);
  const [openRemove, setOpenRemove] = useState("");
  const [generateCode, setGenerateCode] = useState("");
  const [secretCode, setSecretCode] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [openTrialPlanPopup, setOpenTrialPlanPopup] = useState("");
  const [openManageModal, setOpenManageModal] = useState(false);
  const [manageCompany, setManageCompany] = useState(null);
  const [manageLoading, setManageLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [id, setId] = useState(0);
  const [openEnterpriseGiftPopup, setOpenEnterpriseGiftPopup] = useState("");
  // 企業共用配額（管理方案彈窗用）：開窗時抓一次、以 prop 傳給 UsagePanel，非即時 fetch。
  const [enterpriseUsage, setEnterpriseUsage] = useState(null);
  // 年度方案服務容量上限（資料中心 / 任務自動化）：開窗時向 usage-api 抓一次，
  // merge 進無席位卡片的 quota_gb。{ data_center: number, automation: number } | null。
  const [serviceQuotas, setServiceQuotas] = useState(null);
  // 共用配額載入旗標：開窗 / 存檔重載期間為 true，讓 UsagePanel 顯示骨架
  const [usageLoading, setUsageLoading] = useState(false);

  const handleDay = useCallback(() => {
    const startDay = new Date(openTrialPlanPopup.start_date);
    const endDay = new Date(openTrialPlanPopup.expiration_date);
    const daysDiffA =
      Math.floor((+endDay - +startDay) / (1000 * 60 * 60 * 24)) + 1;
    return daysDiffA > 0 ? daysDiffA : "-";
  }, [openTrialPlanPopup.start_date, openTrialPlanPopup.expiration_date]);

  useEffect(() => {
    dispatch(
      getEnterpriseCompany({
        currentPage: page + 1,
        limit: pageLimit,
        keyword: fetchSearchValue,
      })
    );
    setOpenDetail(false);
  }, [dispatch, page, fetchSearchValue]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(
        getEnterpriseCompany({
          currentPage: page + 1,
          limit: pageLimit,
          keyword: fetchSearchValue,
        })
      );

      dispatch(getEnterpriseCompanyListDetail({ id })).then((e) => {
        setDetailData(e?.payload);
        const dataList = Array.isArray(e?.payload?.data) ? e.payload.data : [];
        setDetailPlanData(
          dataList.filter(
            (item) =>
              item?.software_services_id != null
          )
        );
      });

      setTimeout(() => {
        dispatch(removeSuccess());
      }, 1200);

      setTimeout(() => {
        setStatus(null);
      }, 2000);
    }
  }, [dispatch, isSuccess, fetchSearchValue, page, id]);

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        dispatch(removeSuccess());
        setStatus(null);
      }, 1500);
    }
  }, [dispatch, error]);

  const restart = useCallback(() => {
    let parameters;
    if (params?.id) {
      parameters = {
        service_id: params.id,
        keyword: fetchSearchValue,
        currentPage: page + 1,
        limit: pageLimit,
      };
    } else {
      parameters = {
        keyword: fetchSearchValue,
        currentPage: page + 1,
        limit: pageLimit,
      };
    }
    setFetchSearchValue("");
    dispatch(getEnterpriseCompany(parameters));
  }, [params?.id, fetchSearchValue, page, dispatch]);

  const handleStartService = () => {
    let activation_setting = -1;
    if (
      (auth?.roles?.find((item) => item.includes("workspace.admin")) &&
        (status.activation_state === 0 || status.activation_state === false)) ||
      (auth?.workspace_permissions?.length > 0 &&
        permissions?.find((item) =>
          item.includes(
            "tdp.services.enterprise.activate_services_enterprise_bought.center"
          )
        ) &&
        (status.activation_state === 0 || status.activation_state === false))
    ) {
      activation_setting = 1;
    } else if (
      (auth?.roles?.find((item) => item.includes("workspace.admin")) &&
        status.activation_state === 1) ||
      (auth?.workspace_permissions?.length > 0 &&
        permissions?.find((item) =>
          item.includes(
            "tdp.services.enterprise.stop_services_enterprise_bought.center"
          )
        ) &&
        status.activation_state === 1)
    ) {
      activation_setting = 0;
    }

    if (activation_setting !== -1) {
      const postObj = {
        enterprise_id: enterpriseId,
        activation_setting,
        service_id: parseInt(status?.software_services_id),
      };
      if (parseInt(status?.software_services_id) === 8) {
        dispatch(enableStartOnePlan({ enterprise_id: enterpriseId })).then(
          () => {
            dispatch(activateEnterpriseCompany(postObj));
          }
        );
      } else {
        dispatch(activateEnterpriseCompany(postObj));
      }
      setIsOpen(false);
    }
  };

  // enterprise-assets（帳戶儲值餘額/未分配儲值金）只有 START-ONE(id 8) 卡片會用到；
  // 企業沒有這個服務就不打，避免對 bas-backend 做無謂請求（試用等其他操作不該觸發它）。
  const START_ONE_SERVICE_ID = 8;
  const fetchAssetsIfStartOne = (enterpriseId, dataList) => {
    const hasStartOne = (dataList || []).some(
      (item) => item?.software_services_id === START_ONE_SERVICE_ID
    );
    if (hasStartOne) {
      dispatch(getEnterpriseAssets({ enterpriseId }));
    }
  };

  const handleListDetail = (i, id) => {
    setOpenTrialPlanPopup("");
    if (detailData?.enterprise_id !== id) {
      dispatch(getEnterpriseCompanyListDetail({ id })).then((e) => {
        setDetailData(e?.payload);
        const dataList = Array.isArray(e?.payload?.data) ? e.payload.data : [];
        setDetailPlanData(
          dataList.filter(
            (item) =>
              item?.software_services_id != null
          )
        );
        fetchAssetsIfStartOne(id, dataList);
      });
    }
    setIndex(i);
    if (i === index && openDetail === true) {
      setOpenDetail(false);
      return;
    } else setOpenDetail(true);
  };

  // 開啟「管理方案」彈窗：載入該企業的方案詳情（原展開時取得的資料）
  const handleOpenManage = (data) => {
    const companyId = data?.enterprise_id;
    setOpenTrialPlanPopup("");
    setManageCompany(data);
    setDetailData(data);
    setDetailPlanData([]);
    setManageLoading(true);
    setOpenManageModal(true);
    setEnterpriseId(companyId);
    setId(companyId);
    setEnterpriseUsage(null);
    setUsageLoading(true);
    dispatch(getEnterpriseCompanyListDetail({ id: companyId })).then((e) => {
      setDetailData(e?.payload);
      const dataList = Array.isArray(e?.payload?.data) ? e.payload.data : [];
      setDetailPlanData(
        dataList.filter(
          (item) =>
            item?.software_services_id != null
        )
      );
      fetchAssetsIfStartOne(companyId, dataList);
      setManageLoading(false);
    });
    // 共用配額：開窗抓一次帶進 UsagePanel（面板本身不再即時 fetch）
    dispatch(getEnterpriseUsage({ enterprise_id: companyId })).then((e) => {
      setEnterpriseUsage(e?.payload || null);
      setUsageLoading(false);
    });
    // 年度方案服務容量上限：開窗向 usage-api 抓一次（失敗＝空 map，卡片 GB 留白，不阻斷）
    loadServiceQuotas(companyId);
  };

  // 向 usage-api 讀取企業各年度方案服務的容量上限（enterprise_service_quotas ?? 常數）。
  // 回 { data_center: number, automation: number }。usage-api 不可達 / 無權限 → 空 map。
  const loadServiceQuotas = (companyId) => {
    if (!companyId) return;
    setServiceQuotas(null);
    sendGetUsageRequest(`/api/orgs/${companyId}/enterprise-quotas`)
      .then((res) => {
        const map = {};
        (res?.services || []).forEach((s) => {
          if (s?.service_type) map[s.service_type] = s.quota_gb;
        });
        setServiceQuotas(map);
      })
      .catch(() => setServiceQuotas({}));
  };

  // 存檔後重載容量上限，讓卡片 GB 顯示已存值（對齊 reloadUsage 的用途）。
  const reloadServiceQuotas = () => loadServiceQuotas(id);

  // 從個人帳號管理頁點「所屬企業」導向而來時，自動開啟該企業的管理方案彈窗，
  // 同時以公司名稱／統編過濾列表，讓該企業定位到列表中並高亮該列。
  useEffect(() => {
    const pending = location.state?.openEnterprise;
    if (pending?.enterprise_id && !openEnterpriseHandled.current) {
      openEnterpriseHandled.current = true;
      handleOpenManage(pending);
      const keyword = pending.tax_id || pending.enterprise_name || "";
      if (keyword) {
        setSearchTerm(keyword);
        setFetchSearchValue(keyword);
        setPage(0);
      }
      setHighlightEnterpriseId(pending.enterprise_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // 過濾後列表載入完成，滑動到目標企業並短暫高亮
  useEffect(() => {
    if (highlightEnterpriseId && highlightRowRef.current) {
      highlightRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      const timer = setTimeout(() => setHighlightEnterpriseId(null), 2800);
      return () => clearTimeout(timer);
    }
  }, [highlightEnterpriseId, business]);

  const handleCloseManage = () => {
    setOpenManageModal(false);
    setManageCompany(null);
    setOpenTrialPlanPopup("");
    setOpenTrialPlanModal("");
    setId(0);
    setEnterpriseUsage(null);
    setUsageLoading(false);
  };

  // 存完方案卡片後：只重載列表與當前企業的方案詳情，讓卡片/計數即時更新。
  // 不動用量（getEnterpriseUsage），避免存一張方案卡片就把用量管理 section 一起重載變骨架。
  const reloadManageDetail = () => {
    dispatch(
      getEnterpriseCompany({
        currentPage: page + 1,
        limit: pageLimit,
        keyword: fetchSearchValue,
      })
    );
    if (id) {
      dispatch(getEnterpriseCompanyListDetail({ id })).then((e) => {
        setDetailData(e?.payload);
        const dataList = Array.isArray(e?.payload?.data) ? e.payload.data : [];
        setDetailPlanData(
          dataList.filter(
            (item) =>
              item?.software_services_id != null
          )
        );
        fetchAssetsIfStartOne(id, dataList);
      });
    }
  };

  // 存完用量管理後：只重載用量（存檔為使用者動作，非即時 fetch），不動方案卡片。
  const reloadUsage = () => {
    if (!id) return;
    setUsageLoading(true);
    dispatch(getEnterpriseUsage({ enterprise_id: id })).then((e) => {
      setEnterpriseUsage(e?.payload || null);
      setUsageLoading(false);
    });
  };

  const handlePageChange = (e) => {
    setPage(parseInt(e));
    dispatch(
      getEnterpriseCompany({
        service_id: params.id,
        currentPage: e,
        limit: pageLimit,
      })
    );
  };

  const handleVerifyCompany = () => {
    dispatch(putVerifyEmail({ enterprise_id: company?.enterprise_id })).then(
      (e) => {
        setCompany("");
        restart();
      }
    );
  };

  const handleRemoveCompany = () => {
    if (generateCode === secretCode) {
      setErrorCode(false);
      dispatch(
        removeCompany({ enterprise_id: openRemove?.enterprise_id })
      ).then((e) => {
        if (e.payload.msg === "success") {
          restart();
        }
      });
      setOpenRemove("");
    } else {
      setErrorCode(true);
    }
  };

  useEffect(() => {
    let array = ["全部"];

    if (menu?.length > 0) {
      menu
        ?.filter((item) => item.id === 2 || item.id === 7)
        ?.forEach((data) => {
          array.push(getServiceName(data.name));
        });
    }
    setOptions(array);
  }, [menu]);

  const handleDropdown = (e) => {
    dispatch(updateActiveService(e));
    setPage(0);
  };

  return (
    <InnerLayout>
      <div className={styles.dashboard}>
        <div className={styles.dashboardBusinessRight}>
          <div className={styles.dashboardTitle}>企業帳號管理</div>

          {auth?.roles?.find((item) => item.includes("workspace.admin")) ||
          (auth?.workspace_permissions?.length > 0 &&
            permissions?.find((item) =>
              item.includes("enterprise.view_enterprise_list")
            ) &&
            !permission_error) ? (
            <>
              <div className={styles.dashboardListContainer}>
                <div className={styles.dashboardListTitle}>企業帳號列表</div>
                <div className={styles.searchBar}>
                  <div className={styles.dropdownWrapper}>
                    <Dropdown
                      placeholder='全部'
                      value={enterprise.active_service}
                      listItems={options}
                      onChange={(e) => handleDropdown(e)}
                      wide={true}
                    />
                  </div>

                  <InputSearch
                    disabled={
                      fetchSearchValue === "" && business?.totalData === 0
                    }
                    placeholder='搜尋公司名稱或統一編號'
                    value={searchTerm}
                    onKeyPress={(e) => {
                      if (
                        (e.code === "Enter" || e.charCode === 13) &&
                        !loading
                      ) {
                        setFetchSearchValue(searchTerm);
                        setPage(0);
                      }
                    }}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    clickSearchIcon={() => {
                      if (!loading) {
                        setFetchSearchValue(searchTerm);
                        setPage(0);
                      }
                    }}
                    onClear={() => {
                      restart();
                      setSearchTerm("");
                    }}
                    width={"230px"}
                  />
                </div>
              </div>

              {!isGiftSuccess && isSuccess && (
                <Toast
                  show={isSuccess || error}
                  title={
                    error
                      ? `未付款，無法開通「${getServiceName(detail?.name)}」`
                      : status?.activation_state === 0
                      ? `已成功開通「${getServiceName(detail?.name)}」`
                      : status?.activation_state === 1
                      ? `已成功停用「${getServiceName(detail?.name)}」`
                      : null
                  }
                  subtitle={status?.enterprise_name}
                />
              )}

              <Alert
                isOpen={isOpen}
                onConfirm={handleStartService}
                onClose={() => {
                  setIsOpen(false);
                  setStatus(null);
                }}
                rightButtonText={status?.activation_state ? "停用" : "啟用"}
                leftButtonText='取消'
                mainTitle={
                  status?.activation_state === 1
                    ? `停用「${getServiceName(detail?.name)}」?`
                    : `啟用「${getServiceName(detail?.name)}」?`
                }
              />

              <div className={styles.tableContainer}>
                {business?.data?.length > 0 && (
                  <div
                    className={styles.paginationContainer}
                    style={{ paddingTop: 0, paddingBottom: 8 }}>
                    <Pagination
                      onPageChange={(e) => {
                        setPage(parseInt(e.selected));
                      }}
                      pageCount={page + 1}
                      totalData={business?.recordsTotal}
                      onInputChange={handlePageChange}
                      totalPages={Math.ceil(business?.recordsTotal / pageLimit)}
                      limit={pageLimit}
                    />
                  </div>
                )}
                <table>
                  <thead>
                    <tr>
                      <th>公司名稱</th>
                      <th>統一編號</th>
                      <th style={{ minWidth: "90px" }}>驗證</th>
                      <th>購買方案</th>
                      <th>啟用日期</th>
                      <th>使用者名單</th>
                      <th>詳細資訊</th>
                      <th width='8%'></th>
                    </tr>
                  </thead>
                  {loading ? (
                    <LoadingTable row={8} column={10} />
                  ) : (
                    <>
                      {business?.data?.length > 0 ? (
                        <tbody>
                          {business?.data?.map((data, i) => (
                            <React.Fragment key={i}>
                              <tr
                                ref={
                                  data.enterprise_id === highlightEnterpriseId
                                    ? highlightRowRef
                                    : null
                                }
                                style={
                                  data.enterprise_id === highlightEnterpriseId
                                    ? {
                                        background: "#f3effe",
                                        transition: "background .4s ease",
                                      }
                                    : undefined
                                }>
                                <td className={styles.tableTd}>
                                  {data.enterprise_name}
                                </td>
                                <td className={styles.tableTd}>
                                  {data.tax_id}
                                </td>
                                <td className={styles.tableTd}>
                                  {data.email_verified_at ? (
                                    <Tips
                                      detail={`${moment(
                                        data.email_verified_at
                                      ).format("YYYY/MM/DD")} Email已驗證`}
                                      topDiff={3}
                                      leftDiff={20}>
                                      <span
                                        className={`${styles.verifyPill} ${styles.verified}`}>
                                        <FiCheck /> 已驗證
                                      </span>
                                    </Tips>
                                  ) : (
                                    <Tips
                                      detail={`驗證企業帳號`}
                                      topDiff={3}
                                      leftDiff={33}>
                                      <button
                                        type='button'
                                        className={`${styles.verifyPill} ${styles.unverified}`}
                                        onClick={() => setCompany(data)}>
                                        <FiX /> 未驗證
                                      </button>
                                    </Tips>
                                  )}
                                </td>
                                <td className={styles.tableTd}>
                                  {(() => {
                                    const c = getPlanCounts(data);
                                    return (
                                      <div className={styles.planSummary}>
                                        <div className={styles.statusPills}>
                                          <div
                                            className={`${styles.pill} ${styles.pillActive}`}>
                                            <span className={styles.dot} />
                                            已啟用{" "}
                                            <span className={styles.pillCount}>
                                              {c.active}
                                            </span>
                                          </div>
                                          <div
                                            className={`${styles.pill} ${styles.pillTrial}`}>
                                            <span className={styles.dot} />
                                            試用中{" "}
                                            <span className={styles.pillCount}>
                                              {c.trial}
                                            </span>
                                          </div>
                                          <div
                                            className={`${styles.pill} ${styles.pillOff}`}>
                                            <span className={styles.dot} />
                                            未啟用{" "}
                                            <span className={styles.pillCount}>
                                              {c.off}
                                            </span>
                                          </div>
                                        </div>
                                        <button
                                          type='button'
                                          className={styles.manageBtn}
                                          onClick={() => handleOpenManage(data)}>
                                          <AiOutlineSetting /> 管理方案
                                        </button>
                                      </div>
                                    );
                                  })()}
                                </td>
                                <td className={styles.tableTd}>
                                  {data.build_at}
                                </td>
                                <td
                                  className={`${styles.center} ${styles.tableTd}`}>
                                  <Button
                                    type='pure'
                                    icon={<MdPeople />}
                                    onClick={() => {
                                      setIsOpenModalUser(true);
                                      setEnterpriseId(data.enterprise_id);
                                    }}
                                  />
                                </td>
                                <td className={styles.tableTd}>
                                  <div className={styles.detailsBtn}>
                                    <CgLoadbarDoc
                                      color='#5856D6'
                                      fontSize={20}
                                    />
                                    <div
                                      style={{
                                        paddingTop: "7px",
                                      }}>
                                      <Button
                                        type='pure'
                                        icon={<IoChevronDownOutline />}
                                        open={
                                          i === index && openDetail === true
                                        }
                                        onClick={() =>
                                          handleListDetail(
                                            i,
                                            data?.enterprise_id
                                          )
                                        }
                                      />
                                    </div>
                                  </div>
                                </td>
                                <td
                                  className={`${styles.dropLists} ${styles.tableTd}`}>
                                  <div className={styles.actionWrapper}>
                                    {(auth?.roles?.find((item) =>
                                      item.includes("workspace.admin")
                                    ) ||
                                      permissions?.includes(
                                        "tdp.services.enterprise.stop_services_enterprise_bought.center"
                                      )) && (
                                      <div>
                                        <Tips
                                          detail={
                                            new Date(data.end_at) <
                                              new Date() && data.plan
                                              ? "此企業帳號仍在訂閱<br />或試用中，無法刪除"
                                              : "刪除企業帳號"
                                          }>
                                          <Button
                                            disabled={
                                              data.subscribed_services?.length >
                                              0
                                            }
                                            type='pure'
                                            icon={<LuTrash2 />}
                                            onClick={() => {
                                              setGenerateCode(
                                                generateRandomString(12)
                                              );
                                              setSecretCode("");
                                              setOpenRemove(data);
                                              setErrorCode(false);
                                            }}
                                            color='#ff0000'
                                          />
                                        </Tips>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>

                              {i === index && openDetail === true && (
                                <>
                                  <tr>
                                    <td
                                      className={styles.detailsBlock}
                                      colSpan='12'>
                                      {openDetail && i === index && (
                                        <div className={styles.details}>
                                          <div
                                            className={
                                              styles.detailsListContainer
                                            }>
                                            <div
                                              className={
                                                styles.detailsListTitle
                                              }>
                                              電子郵件
                                            </div>
                                            <div className={styles.detailsList}>
                                              {data.email}
                                            </div>
                                          </div>
                                          <div
                                            className={
                                              styles.detailsListContainer
                                            }>
                                            <div
                                              className={
                                                styles.detailsListTitle
                                              }>
                                              公司電話
                                            </div>
                                            <div className={styles.detailsList}>
                                              {data.phone_country} {data.phone}
                                            </div>
                                          </div>
                                          <div
                                            className={
                                              styles.detailsListContainer
                                            }>
                                            <div
                                              className={
                                                styles.detailsListTitle
                                              }>
                                              聯繫人姓名
                                            </div>
                                            <div className={styles.detailsList}>
                                              {data.contact_name
                                                ? data.contact_name
                                                : "-"}
                                            </div>
                                          </div>
                                          <div
                                            className={
                                              styles.detailsListContainer
                                            }>
                                            <div
                                              className={
                                                styles.detailsListTitle
                                              }>
                                              聯繫人職稱
                                            </div>
                                            <div className={styles.detailsList}>
                                              {data.job}
                                            </div>
                                          </div>
                                          <div
                                            className={
                                              styles.detailsListContainer
                                            }>
                                            <div
                                              className={
                                                styles.detailsListTitle
                                              }>
                                              聯繫人性別
                                            </div>
                                            <div className={styles.detailsList}>
                                              {!data.contact_gender && "-"}
                                              {data.contact_gender === "male" &&
                                                "男性"}
                                              {data.contact_gender ===
                                                "female" && "女性"}
                                              {data.contact_gender ===
                                                "other" && "其他"}
                                            </div>
                                          </div>
                                          <div
                                            className={
                                              styles.detailsListContainer
                                            }>
                                            <div
                                              className={
                                                styles.detailsListTitle
                                              }>
                                              聯繫人電話
                                            </div>
                                            <div className={styles.detailsList}>
                                              {data.cellphone_country}
                                              {data.cell_phone}
                                            </div>
                                          </div>
                                          <div
                                            className={
                                              styles.detailsListContainer
                                            }>
                                            <div
                                              className={
                                                styles.detailsListTitle
                                              }>
                                              目前最高人數
                                            </div>
                                            <div className={styles.detailsList}>
                                              {detailPlanData?.length !== 0 &&
                                                formatSeatLimit(
                                                  detailPlanData[0]
                                                    ?.users_count_upper_limit
                                                )}
                                            </div>
                                          </div>
                                          <div
                                            className={
                                              styles.detailsListContainer
                                            }>
                                            {detailPlanData?.length !== 0 && (
                                              <>
                                                <div
                                                  className={
                                                    styles.detailsListTitle
                                                  }>
                                                  付款方式
                                                </div>
                                                <div
                                                  className={
                                                    styles.detailsList
                                                  }>
                                                  {detailPlanData[0]?.payment}
                                                  <div>
                                                    {
                                                      detailPlanData[0]
                                                        ?.start_date
                                                    }{" "}
                                                    ~{" "}
                                                    {
                                                      detailPlanData[0]
                                                        ?.expiration_date
                                                    }{" "}
                                                    (
                                                    {
                                                      detailPlanData[0]?.plan
                                                        ?.validity_days
                                                    }
                                                    天)
                                                  </div>
                                                </div>
                                              </>
                                            )}
                                          </div>
                                          <div
                                            className={`${styles.detailsListContainer} ${styles.first}`}>
                                            <div
                                              className={
                                                styles.detailsListTitle
                                              }>
                                              官網 Admin
                                            </div>
                                            <div className={styles.detailsList}>
                                              {data.admins?.map(
                                                (admin, index) => (
                                                  <div key={index}>
                                                    {admin.contact_name}{" "}
                                                    <small>
                                                      [{admin.email}]
                                                    </small>
                                                  </div>
                                                )
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      )}

                                    </td>
                                  </tr>
                                </>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      ) : (
                        <tbody>
                          <tr>
                            <td colSpan={11}>
                              <EmptyBox
                                caption={
                                  fetchSearchValue
                                    ? "目前尚無搜尋結果"
                                    : "目前沒有企業帳號管理"
                                }
                              />
                            </td>
                          </tr>
                        </tbody>
                      )}
                    </>
                  )}
                </table>

                {business?.data?.length > 0 && (
                  <div className={styles.paginationContainer}>
                    <Pagination
                      onPageChange={(e) => {
                        setPage(parseInt(e.selected));
                      }}
                      pageCount={page + 1}
                      totalData={business?.recordsTotal}
                      onInputChange={handlePageChange}
                      totalPages={Math.ceil(business?.recordsTotal / pageLimit)}
                      limit={pageLimit}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className={styles.tableContainer}>
              <EmptyBox caption={"您沒有權限"} />
            </div>
          )}
        </div>
      </div>

      <ManagePlanModal
        isOpen={openManageModal}
        onClose={handleCloseManage}
        company={manageCompany}
        detailData={detailData}
        detailPlanData={detailPlanData}
        loading={manageLoading}
        usageData={enterpriseUsage}
        usageLoading={usageLoading}
        serviceQuotas={serviceQuotas}
        planDataProps={{
          auth,
          permissions,
          setIsOpen,
          setStatus,
          setEnterpriseId,
          dispatch,
          openTrialPlanPopup,
          setOpenTrialPlanPopup,
          openTrialPlanModal,
          setOpenTrialPlanModal,
          handleDay,
          openEnterpriseGiftPopup,
          setOpenEnterpriseGiftPopup,
          restart,
          reloadDetail: reloadManageDetail,
          reloadUsage,
          reloadQuotas: reloadServiceQuotas,
          setIsOpenModalUser,
          setServiceId,
          setIsOpenModal,
          params,
          enterpriseAssets,
          menu,
        }}
      />

      <GiftModal
        openGiftModal={openGiftModal}
        setOpenGiftModal={setOpenGiftModal}
        restart={() => {
          dispatch(
            getEnterpriseCompany({
              service_id: params.id,
              currentPage: page + 1,
              limit: pageLimit,
              keyword: fetchSearchValue,
            })
          );
          dispatch(getEnterpriseCompanyListDetail({ id })).then((e) => {
            setDetailData(e?.payload);
            const dataList = Array.isArray(e?.payload?.data)
              ? e.payload.data
              : [];
            setDetailPlanData(
              dataList.filter(
                (item) =>
                  item?.software_services_id != null
              )
            );
          });
        }}
      />

      {isGiftPointsSuccess && (
        <Toast
          show={isGiftPointsSuccess}
          title={"已完成贈送點數"}
          subtitle={``}
          error={false}
        />
      )}

      {isOpenModalUser && (
        <ModalUsers
          isOpen={isOpenModalUser}
          setIsOpen={setIsOpenModalUser}
          enterpriseId={enterpriseId}
          setEnterpriseId={setEnterpriseId}
          serviceId={serviceId}
          setServiceId={setServiceId}
        />
      )}

      {isOpenModal && (
        <ModalHistory
          isOpen={isOpenModal}
          setIsOpen={setIsOpenModal}
          enterpriseId={enterpriseId}
          setEnterpriseId={setEnterpriseId}
          serviceId={serviceId}
          setServiceId={setServiceId}
        />
      )}

      {company && (
        <Alert
          isOpen={company}
          onConfirm={handleVerifyCompany}
          onClose={() => setCompany("")}
          rightButtonText={"驗證"}
          leftButtonText='取消'
          mainTitle={`確認驗證${company?.enterprise_name}？`}
        />
      )}

      {openRemove && (
        <Alert
          isWarning={true}
          isOpen={openRemove}
          onConfirm={handleRemoveCompany}
          onClose={() => setOpenRemove("")}
          rightButtonText={"確認刪除"}
          leftButtonText='取消'
          mainTitle={`確認刪除「${openRemove?.enterprise_name}」帳號？`}>
          <div className={styles.formSubtitle}>請輸入確認代碼</div>
          <div className={styles.formGroup}>確認代碼： {generateCode}</div>
          <TextField
            placeholder={"請輸入確認代碼"}
            onChange={(e) => {
              setErrorCode(false);
              setSecretCode(e);
            }}
            fullWidth
            value={secretCode}
            error={errorCode && secretCode !== generateCode}
          />
          {errorCode && secretCode !== generateCode && (
            <div className={styles.error}>代碼有錯誤</div>
          )}
        </Alert>
      )}
    </InnerLayout>
  );
};

export default EnterpriseList;
